/**
 * useVoiceChannelPresence Hook
 *
 * Tracks users in voice/media channels with:
 * - Real-time user list per channel
 * - User count badges
 * - Session timers (how long channel has been active)
 * - Analytics tracking for creators/businesses
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

/**
 * @typedef {Object} ChannelUser
 * @property {string} id - User ID
 * @property {string} username - Username
 * @property {string} display_name - Display name
 * @property {string} avatar_url - Avatar URL
 * @property {boolean} isMuted - Is user muted
 * @property {boolean} isDeafened - Is user deafened
 * @property {string} joinedAt - When user joined
 */

/**
 * @typedef {Object} ChannelPresence
 * @property {ChannelUser[]} users - Users in channel
 * @property {number} count - User count
 * @property {string|null} sessionStarted - When first user joined (ISO string)
 * @property {string|null} sessionId - Current session ID for analytics
 */

export function useVoiceChannelPresence(spaceId, channels = []) {
  // Map of channelId -> ChannelPresence
  const [channelPresence, setChannelPresence] = useState({})
  const [loading, setLoading] = useState(true)

  // Track active sessions
  const activeSessionsRef = useRef({})

  // Get voice/media channels only
  const voiceChannels = channels.filter(c => c.type === 'voice' || c.type === 'media')

  /**
   * Start a new session when first user joins a channel
   */
  const startSession = useCallback(async (channelId) => {
    if (!spaceId || activeSessionsRef.current[channelId]) return null

    try {
      const { data, error } = await supabase
        .from('channel_sessions')
        .insert({
          channel_id: channelId,
          space_id: spaceId,
          started_at: new Date().toISOString(),
          is_active: true,
          peak_users: 1
        })
        .select('id')
        .single()

      if (error) {
        console.error('[VoicePresence] Failed to start session:', error)
        return null
      }

      activeSessionsRef.current[channelId] = data.id
      return data.id
    } catch (err) {
      console.error('[VoicePresence] Error starting session:', err)
      return null
    }
  }, [spaceId])

  /**
   * End session when last user leaves
   */
  const endSession = useCallback(async (channelId) => {
    const sessionId = activeSessionsRef.current[channelId]
    if (!sessionId) return

    try {
      await supabase
        .from('channel_sessions')
        .update({
          ended_at: new Date().toISOString(),
          is_active: false
        })
        .eq('id', sessionId)

      delete activeSessionsRef.current[channelId]
    } catch (err) {
      console.error('[VoicePresence] Error ending session:', err)
    }
  }, [])

  /**
   * Update peak users count
   */
  const updatePeakUsers = useCallback(async (channelId, count) => {
    const sessionId = activeSessionsRef.current[channelId]
    if (!sessionId) return

    try {
      // Only update if new count is higher than current peak
      await supabase.rpc('update_session_peak_users', {
        p_session_id: sessionId,
        p_user_count: count
      })
    } catch (err) {
      // RPC might not exist yet, that's ok
      console.warn('[VoicePresence] Could not update peak users:', err)
    }
  }, [])

  /**
   * Fetch current users in voice/media channels
   */
  const fetchChannelUsers = useCallback(async () => {
    if (!spaceId || voiceChannels.length === 0) {
      setLoading(false)
      return
    }

    try {
      const channelIds = voiceChannels.map(c => c.id)

      // Query room_members for users in these channels
      const { data: members, error } = await supabase
        .from('room_members')
        .select('room_id, user_id, joined_at')
        .in('room_id', channelIds)

      if (error) {
        console.error('[VoicePresence] Error fetching members:', error)
        setLoading(false)
        return
      }

      // Fetch profiles separately for the user IDs
      const userIds = [...new Set(members?.map(m => m.user_id) || [])]
      let profilesMap = {}

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url')
          .in('id', userIds)

        profilesMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p
          return acc
        }, {})
      }

      // Also check for active sessions
      const { data: sessions } = await supabase
        .from('channel_sessions')
        .select('id, channel_id, started_at, peak_users')
        .in('channel_id', channelIds)
        .eq('is_active', true)

      // Build presence map
      const presenceMap = {}

      for (const channel of voiceChannels) {
        const channelMembers = members?.filter(m => m.room_id === channel.id) || []
        const activeSession = sessions?.find(s => s.channel_id === channel.id)

        // Find earliest join time for session start
        let sessionStarted = null
        if (channelMembers.length > 0) {
          const earliestJoin = channelMembers.reduce((earliest, m) => {
            const joinTime = new Date(m.joined_at)
            return !earliest || joinTime < earliest ? joinTime : earliest
          }, null)
          sessionStarted = earliestJoin?.toISOString() || activeSession?.started_at
        }

        presenceMap[channel.id] = {
          users: channelMembers.map(m => {
            const profile = profilesMap[m.user_id]
            return {
              id: m.user_id,
              username: profile?.username || 'Unknown',
              display_name: profile?.display_name || profile?.username || 'Unknown',
              avatar_url: profile?.avatar_url,
              isMuted: false, // TODO: Get from voice state
              isDeafened: false,
              joinedAt: m.joined_at
            }
          }),
          count: channelMembers.length,
          sessionStarted,
          sessionId: activeSession?.id || null
        }

        // Track active sessions
        if (activeSession) {
          activeSessionsRef.current[channel.id] = activeSession.id
        }
      }

      setChannelPresence(presenceMap)
      setLoading(false)
    } catch (err) {
      console.error('[VoicePresence] Error:', err)
      setLoading(false)
    }
  }, [spaceId, voiceChannels])

  /**
   * Subscribe to realtime changes
   */
  useEffect(() => {
    if (!spaceId || voiceChannels.length === 0) return

    // Initial fetch
    fetchChannelUsers()

    const channelIds = voiceChannels.map(c => c.id)

    // Subscribe to room_members changes
    const subscription = supabase
      .channel(`voice-presence-${spaceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'room_members',
          filter: `room_id=in.(${channelIds.join(',')})`
        },
        async (payload) => {
          console.log('[VoicePresence] Change detected:', payload.eventType)

          // Refetch to get updated data
          await fetchChannelUsers()

          // Handle session tracking
          const channelId = payload.new?.room_id || payload.old?.room_id
          if (channelId) {
            const presence = channelPresence[channelId]

            if (payload.eventType === 'INSERT') {
              // User joined - start session if first user
              if (!activeSessionsRef.current[channelId]) {
                await startSession(channelId)
              }
              // Update peak users
              if (presence && presence.count > 0) {
                await updatePeakUsers(channelId, presence.count + 1)
              }
            } else if (payload.eventType === 'DELETE') {
              // User left - end session if last user
              const remaining = presence?.count - 1 || 0
              if (remaining <= 0) {
                await endSession(channelId)
              }
            }
          }
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [spaceId, voiceChannels.length, fetchChannelUsers, startSession, endSession, updatePeakUsers])

  /**
   * Get formatted data for ChannelsSidebar
   */
  const getChannelUsers = useCallback(() => {
    const result = {}
    for (const [channelId, presence] of Object.entries(channelPresence)) {
      result[channelId] = presence.users
    }
    return result
  }, [channelPresence])

  /**
   * Get user count for a channel
   */
  const getChannelCount = useCallback((channelId) => {
    return channelPresence[channelId]?.count || 0
  }, [channelPresence])

  /**
   * Get session start time for a channel (for timer display)
   */
  const getSessionStart = useCallback((channelId) => {
    return channelPresence[channelId]?.sessionStarted || null
  }, [channelPresence])

  /**
   * Check if channel has active users
   */
  const isChannelActive = useCallback((channelId) => {
    return (channelPresence[channelId]?.count || 0) > 0
  }, [channelPresence])

  return {
    channelPresence,
    channelUsers: getChannelUsers(),
    getChannelCount,
    getSessionStart,
    isChannelActive,
    loading,
    refetch: fetchChannelUsers
  }
}

export default useVoiceChannelPresence
